import React, { useState, useEffect } from 'react'
import {
    Input,
    type InputProps,
} from '@chakra-ui/react'
import { Text } from '@chakra-ui/react'
import InputMask from '@mona-health/react-input-mask'

import {
    getCountryCode,
    getCountryCodeFlag,
    getCountryCodeMask,
} from '~utils/constants/intl-phones'
import MenuSelect from './menu-select'
import { countries, Country } from '~utils/constants/countries'
import { validatePhoneForFirebaseAuth } from '~utils/helpers'
import SharedStack from '~components/shared/wrappers/shared-stack'

interface Props extends InputProps {
    value: string
    setValue: (v: string) => void
    showValidation?: boolean
    /** Optional: provide a default country (overrides auto-detection) */
    defaultCountry?: Country
    /** Optional: callback when the country changes (for forms that need to track country) */
    onCountryChange?: (country: Country) => void
}

/**
 * True when digits are a 10-digit NANP national number (or 11-digit with leading 1).
 * Prevents US/CA area codes that share prefixes with foreign calling codes
 * (650→SG +65, 61x→AU +61, 64x→NZ +64, 27x→ZA +27) from being auto-detected
 * as international.
 */
function isNanpShaped(digits: string): boolean {
    const national =
        digits.length === 11 && digits.startsWith('1')
            ? digits.slice(1)
            : digits.length === 10
              ? digits
              : null

    return national !== null && /^[2-9]\d{2}[2-9]\d{6}$/.test(national)
}

/**
 * Detects the country from a phone number based on country code prefix.
 * Returns 'United States' as the default if no country code is detected.
 */
function detectCountryFromPhone(phone: string | null | undefined): Country {
    if (!phone) return 'United States'

    const cleaned = phone.replace(/\D/g, '')

    // NANP-shaped numbers are US/CA — check before foreign calling-code prefixes
    if (isNanpShaped(cleaned)) {
        return 'United States'
    }

    // Foreign E.164-without-+ storage: calling code + national number
    if (
        cleaned.startsWith('61') &&
        (cleaned.length === 11 || cleaned.length === 10)
    ) {
        return 'Australia'
    }
    if (
        cleaned.startsWith('64') &&
        (cleaned.length === 10 || cleaned.length === 11)
    ) {
        return 'New Zealand'
    }
    if (cleaned.startsWith('65') && cleaned.length === 10) {
        const national = cleaned.slice(2)
        // SG national numbers are 8 digits starting with 6, 8, or 9
        if (['6', '8', '9'].includes(national[0])) {
            return 'Singapore'
        }
    }
    if (cleaned.startsWith('27') && cleaned.length === 11) {
        return 'South Africa'
    }
    if (cleaned.startsWith('1') && cleaned.length === 11) {
        return 'United States'
    }

    return 'United States'
}

export default function InternationalPhoneInput({
    value,
    setValue,
    showValidation = false,
    defaultCountry,
    onCountryChange,
    ...rest
}: Props) {
    // Initialize country from phone number or default
    const [country, setCountryState] = useState<Country>(
        defaultCountry || detectCountryFromPhone(value)
    )

    // Wrapper to also call the callback when country changes
    const setCountry = (newCountry: Country) => {
        setCountryState(newCountry)
        onCountryChange?.(newCountry)
    }

    // Update country when phone changes and has a detectable country code
    useEffect(() => {
        if (value && !defaultCountry) {
            const detectedCountry = detectCountryFromPhone(value)
            // Only auto-update if the phone clearly has a country code.
            // Skip NANP-shaped numbers — their leading digits can collide with
            // foreign calling codes (e.g. US 650 vs Singapore +65).
            const cleaned = value.replace(/\D/g, '')
            const hasCountryCode =
                !isNanpShaped(cleaned) &&
                (cleaned.startsWith('61') ||
                    cleaned.startsWith('64') ||
                    (cleaned.startsWith('65') &&
                        cleaned.length === 10 &&
                        ['6', '8', '9'].includes(cleaned[2])) ||
                    (cleaned.startsWith('27') && cleaned.length === 11) ||
                    (cleaned.startsWith('1') && cleaned.length === 11))

            if (hasCountryCode && detectedCountry !== country) {
                setCountry(detectedCountry)
            }
        }
    }, [value, defaultCountry])

    const isValid = validatePhoneForFirebaseAuth(value, country)
    const borderColor =
        showValidation && value && !isValid ? 'red.500' : undefined
    const countryLower = country.toLowerCase()
    const needsFormatting = ['australia', 'new zealand', 'singapore', 'south africa', 'united states', 'canada'].includes(countryLower)

    return (
        <SharedStack spacing={0} row fit>
            <CountrySelect country={country} setCountry={setCountry} />
            <Input
                as={InputMask}
                type="tel"
                autoComplete="tel"
                borderLeftRadius={0}
                placeholder={getCountryCodeMask(country)}
                mask={getCountryCodeMask(country)}
                maskPlaceholder=""
                maskChar=""
                value={
                    needsFormatting
                        ? getInternationalPhoneValue(value, countryLower)
                        : value
                }
                onChange={e => {
                    if (needsFormatting) {
                        setValue(parseInternationalPhoneValue(e.target.value, countryLower))
                        return
                    }
                    setValue(e.target.value)
                }}
                borderColor={borderColor}
                {...rest}
            />
        </SharedStack>
    )
}

/**
 * Convert internal storage format to display format for international numbers.
 * Supports: United States (+1), Canada (+1), Australia (+61), New Zealand (+64), Singapore (+65), South Africa (+27)
 */
function getInternationalPhoneValue(phone: string, countryLower: string): string {
    const cleaned = phone.replace(/\D/g, '')

    if (countryLower === 'united states' || countryLower === 'canada') {
        // If it's already in international format with 1
        if (cleaned.startsWith('1') && cleaned.length === 11) {
            const numberPart = cleaned.slice(1)
            // Format as (XXX) XXX-XXXX
            return numberPart.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
        }

        // If it's a 10-digit number
        if (cleaned.length === 10) {
            // Format as (XXX) XXX-XXXX
            return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
        }

        return phone
    }

    if (countryLower === 'australia') {
        // If it's already in international format with 61
        if (cleaned.startsWith('61')) {
            const numberPart = cleaned.slice(2)
            if (numberPart.length === 9) {
                // Mobile number: 0XXX XXX XXX
                return '0' + numberPart.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
            } else if (numberPart.length === 8) {
                // Landline: 0X XXXX XXXX
                return '0' + numberPart.replace(/(\d{1})(\d{4})(\d{4})/, '$1 $2 $3')
            }
        }

        // If it's a 9-digit mobile number
        if (cleaned.length === 9 && cleaned.startsWith('4')) {
            return '0' + cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
        }

        return phone
    }

    if (countryLower === 'new zealand') {
        // If it's already in international format with 64
        if (cleaned.startsWith('64')) {
            const numberPart = cleaned.slice(2)
            if (numberPart.length >= 8 && numberPart.length <= 9) {
                // Mobile number: 0XX XXX XXXX
                return '0' + numberPart.replace(/(\d{2})(\d{3})(\d{3,4})/, '$1 $2 $3')
            }
        }

        // If it's an 8-9 digit mobile number starting with 2
        if ((cleaned.length === 8 || cleaned.length === 9) && cleaned.startsWith('2')) {
            return '0' + cleaned.replace(/(\d{2})(\d{3})(\d{3,4})/, '$1 $2 $3')
        }

        return phone
    }

    if (countryLower === 'singapore') {
        // International format with 65 only for valid SG nationals (6/8/9),
        // not US NANP area codes like 650.
        if (cleaned.startsWith('65') && cleaned.length === 10) {
            const numberPart = cleaned.slice(2)
            if (['6', '8', '9'].includes(numberPart[0])) {
                return numberPart.replace(/(\d{4})(\d{4})/, '$1 $2')
            }
        }

        // If it's an 8-digit number
        if (cleaned.length === 8 && ['6', '8', '9'].includes(cleaned[0])) {
            return cleaned.replace(/(\d{4})(\d{4})/, '$1 $2')
        }

        return phone
    }

    if (countryLower === 'south africa') {
        // If it's already in international format with 27
        if (cleaned.startsWith('27')) {
            const numberPart = cleaned.slice(2)
            if (numberPart.length === 9) {
                // Mobile number: 0XX XXX XXXX
                return '0' + numberPart.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
            }
        }

        // If it's a 9-digit mobile number
        if (cleaned.length === 9) {
            return '0' + cleaned.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
        }

        return phone
    }

    return phone
}

/**
 * Convert display format to internal storage format for international numbers.
 * Supports: United States (+1), Canada (+1), Australia (+61), New Zealand (+64), Singapore (+65), South Africa (+27)
 */
function parseInternationalPhoneValue(phone: string, countryLower: string): string {
    // Remove leading 0 and all non-digits
    const cleaned = phone.replace(/\D/g, '').replace(/^0/, '')

    if (countryLower === 'united states' || countryLower === 'canada') {
        // If it's already in international format with 1
        if (cleaned.startsWith('1') && cleaned.length === 11) {
            return cleaned
        }

        // If it's a 10-digit number, prepend country code
        if (cleaned.length === 10) {
            return '1' + cleaned
        }

        return cleaned
    }

    if (countryLower === 'australia') {
        // If it's already in international format with 61
        if (cleaned.startsWith('61')) {
            return cleaned
        }

        // If it's a 9-digit mobile number (starting with 4) or 8-digit landline
        if ((cleaned.length === 9 && cleaned.startsWith('4')) || cleaned.length === 8) {
            return '61' + cleaned
        }

        return cleaned
    }

    if (countryLower === 'new zealand') {
        // If it's already in international format with 64
        if (cleaned.startsWith('64')) {
            return cleaned
        }

        // If it's an 8-9 digit number starting with 2
        if ((cleaned.length === 8 || cleaned.length === 9) && cleaned.startsWith('2')) {
            return '64' + cleaned
        }

        return cleaned
    }

    if (countryLower === 'singapore') {
        if (
            cleaned.startsWith('65') &&
            cleaned.length === 10 &&
            ['6', '8', '9'].includes(cleaned[2])
        ) {
            return cleaned
        }

        // If it's an 8-digit number, prepend country code
        if (cleaned.length === 8 && ['6', '8', '9'].includes(cleaned[0])) {
            return '65' + cleaned
        }

        return cleaned
    }

    if (countryLower === 'south africa') {
        // If it's already in international format with 27
        if (cleaned.startsWith('27')) {
            return cleaned
        }

        // If it's a 9-digit number
        if (cleaned.length === 9) {
            return '27' + cleaned
        }

        return cleaned
    }

    return phone
}

function CountrySelect({
    country,
    setCountry,
}: {
    country: Country
    setCountry: (country: Country) => void
}) {
    function mapCountriesToOptions() {
        return countries.map(c => ({
            value: c.name,
            label: c.name,
            leftElement: (
                <Text fontSize="xl">
                    {getCountryCodeFlag(c.name as Country)}
                </Text>
            ),
            rightElement: (
                <Text
                    fontSize="sm"
                    color="gray.500"
                    _dark={{ color: 'gray.300' }}
                >
                    {getCountryCode(c.name as Country)}
                </Text>
            ),
        }))
    }

    return (
        <MenuSelect
            options={mapCountriesToOptions()}
            value={country}
            onChange={c => setCountry(c as Country)}
            placeholder={getCountryCodeFlag('United States')}
            buttonOverrides={{ px: 3, borderRightRadius: 0, borderRightWidth: 0 }}
            renderValue={c => (
                <Text fontSize="xl" mr={2}>
                    {getCountryCodeFlag(c as Country)}
                </Text>
            )}
        />
    )
}
